import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Autocomplete from '@mui/material/Autocomplete';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';

import { fetchCuratedList, fetchCuratedInfluencers } from 'api_handlers/planner2';
import { getCampaigns, getCampaignCreators } from 'api_handlers/campaign';
import { getDiscoverySearchResultv2 } from 'api_handlers/es';
import { getInfluencerBriefs } from 'api_handlers/discovery';
import DiscoverThemeProvider from '../InfluencerDiscoverV2/DiscoverThemeProvider';
import AudienceUniqueReachModal from '../InfluencerDiscoverV2/AudienceUniqueReachModal';
import '../InfluencerDiscoverV2/discoverV2.css';

const MAX = 10;
const NAVY = '#111936';
const GOLD = '#febd1c';

function normalize(item, fallbackPlatform) {
  if (!item || typeof item !== 'object') return null;
  const id = item.id || item.iq_id || item.profile_id || item.iqprofile_id;
  if (!id) return null;
  return {
    id,
    name: item.name || item.display_name || item.username || item.handle || `#${id}`,
    picture: item.picture || item.profile_pic || item.image || item.avatar || '',
    platform: (item.platform || fallbackPlatform || '').toLowerCase(),
    followers: item.followers ?? item.follower_count ?? null,
    engagement_rate: item.engagement_rate ?? item.er ?? null,
    avg_views: item.avg_views ?? null,
    language: item.language ?? null,
    location: item.location ?? item.city ?? item.country ?? null,
  };
}

const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : v);

// Follower / view counts: numbers -> "1.2 M"; already-formatted strings pass through.
const fmtCount = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : (String(v).match(/[a-zA-Z]/) ? NaN : Number(v));
  if (Number.isNaN(n)) return v;
  if (n >= 1e7) return `${(n / 1e7).toFixed(1)} Cr`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)} K`;
  return `${n}`;
};
// Engagement rate: a fraction (0-1) -> "%", a bare number -> "%", strings pass through.
const fmtER = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return `${(v <= 1.5 ? v * 100 : v).toFixed(1)}%`;
  return String(v).includes('%') ? v : `${v}%`;
};

// Platform label with its logo, left-aligned under the creator name.
function PlatformTag({ platform }) {
  const p = (platform || '').toLowerCase();
  const wrap = { display: 'inline-flex', alignItems: 'center', gap: 0.4 };
  const label = { fontSize: 11.5, color: '#98a2b3', lineHeight: 1 };
  if (p.includes('you')) {
    return (
      <Box sx={wrap}>
        <YouTubeIcon sx={{ fontSize: 15, color: '#FF0000' }} />
        <Typography component="span" sx={label}>YouTube</Typography>
      </Box>
    );
  }
  if (p.includes('insta')) {
    return (
      <Box sx={wrap}>
        <InstagramIcon sx={{ fontSize: 14, color: '#E1306C' }} />
        <Typography component="span" sx={label}>Instagram</Typography>
      </Box>
    );
  }
  return <Typography sx={label}>{platform}</Typography>;
}

const ROW_COLS = '1fr 80px 58px 84px 86px 96px 76px';

// One creator row — laid out as columns to match the Discovery table.
function CreatorRow({ c, onAdd, added, disabled }) {
  return (
    <Box className="dv2-row"
      sx={{
        display: 'grid',
        gridTemplateColumns: ROW_COLS,
        alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
        borderBottom: '1px solid #f2f3f6',
        '&:hover': { background: 'rgba(17,25,54,0.035)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        <Avatar src={c.picture} sx={{ width: 34, height: 34 }}>{(c.name || '?').slice(0, 1)}</Avatar>
        <Box sx={{ minWidth: 0, textAlign: 'left' }}>
          <Typography noWrap sx={{ fontWeight: 600, fontSize: 13.5, color: NAVY }}>{c.name}</Typography>
          <PlatformTag platform={c.platform} />
        </Box>
      </Box>
      <Typography sx={{ fontSize: 13, color: NAVY, fontWeight: 600 }}>{fmtCount(c.followers)}</Typography>
      <Box>
        {fmtER(c.engagement_rate) ? (
          <Chip size="small" label={fmtER(c.engagement_rate)}
            sx={{ bgcolor: 'rgba(254,189,28,0.16)', color: '#8a6d00', fontWeight: 700, height: 22 }} />
        ) : <Typography sx={{ fontSize: 13, color: '#98a2b3' }}>—</Typography>}
      </Box>
      <Typography noWrap sx={{ fontSize: 13, color: '#475467' }}>{fmtCount(c.avg_views)}</Typography>
      <Typography noWrap sx={{ fontSize: 13, color: '#475467' }}>{fmt(c.language)}</Typography>
      <Typography noWrap sx={{ fontSize: 13, color: '#475467' }}>{fmt(c.location)}</Typography>
      <Button size="small" variant={added ? 'text' : 'contained'} color="primary"
        disabled={disabled || added} startIcon={added ? <CheckIcon /> : <AddIcon />}
        onClick={() => onAdd(c)} sx={{ minWidth: 0 }}>
        {added ? 'Added' : 'Add'}
      </Button>
    </Box>
  );
}

function TableHeader() {
  const cell = { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.4px', color: '#98a2b3', textTransform: 'uppercase' };
  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: ROW_COLS,
      gap: 1, px: 1.5, py: 1, borderBottom: '1px solid #e7e9ee', position: 'sticky', top: 0,
      background: '#fafbfc', zIndex: 1,
    }}>
      <Typography sx={cell}>Creator</Typography>
      <Typography sx={cell}>Followers</Typography>
      <Typography sx={cell}>ER</Typography>
      <Typography sx={cell}>Avg Views</Typography>
      <Typography sx={cell}>Language</Typography>
      <Typography sx={cell}>Location</Typography>
      <Typography sx={cell}> </Typography>
    </Box>
  );
}

// Decorative overlap graphic for the hero (audience Venn).
function HeroGraphic() {
  return (
    <Box sx={{ display: { xs: 'none', md: 'block' }, opacity: 0.9 }}>
      <svg width="150" height="88" viewBox="0 0 150 88" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="55" cy="44" r="34" fill={GOLD} fillOpacity="0.22" stroke={GOLD} strokeOpacity="0.5" />
        <circle cx="95" cy="44" r="34" fill="#fff" fillOpacity="0.12" stroke="#fff" strokeOpacity="0.4" />
        <circle cx="75" cy="26" r="24" fill={GOLD} fillOpacity="0.14" stroke={GOLD} strokeOpacity="0.35" />
      </svg>
    </Box>
  );
}

export default function UniqueReachSection() {
  const [platform, setPlatform] = useState('instagram');
  const [tab, setTab] = useState('search');
  const [selected, setSelected] = useState([]);
  const [openCompare, setOpenCompare] = useState(false);

  // search
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!query || query.trim().length < 2) { setSearchResults([]); return undefined; }
    const h = setTimeout(async () => {
      try {
        if (cancelRef.current) cancelRef.current.cancel();
        cancelRef.current = axios.CancelToken.source();
        setSearching(true);
        const data = await getDiscoverySearchResultv2(query.trim(), cancelRef.current, platform);
        const raw = (data && (data.result || data.results || data.data)) || [];
        const rows = raw.map((r) => normalize(r, platform)).filter(Boolean);
        // Search only returns id/name/platform/language — enrich the numeric
        // columns (followers/ER/avg views) from batch-brief so the table isn't blank.
        try {
          const ids = rows.map((r) => r.id);
          if (ids.length) {
            const bd = await getInfluencerBriefs(ids);
            const briefs = (bd && (bd.results || bd.influencers || bd.data)) || [];
            const byId = {};
            briefs.forEach((b) => { byId[b.id] = b; });
            rows.forEach((r) => {
              const b = byId[r.id];
              if (b) {
                r.followers = b.followers ?? r.followers;
                r.engagement_rate = b.engagement_rate ?? r.engagement_rate;
                r.avg_views = b.avg_views ?? r.avg_views;
                r.location = b.location ?? r.location;
              }
            });
          }
        } catch (e) { /* enrichment is best-effort */ }
        setSearchResults(rows);
      } catch (e) { if (!axios.isCancel(e)) setSearchResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(h);
  }, [query, platform]);

  // campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [campaignCreators, setCampaignCreators] = useState([]);
  const [loadingCampaign, setLoadingCampaign] = useState(false);

  useEffect(() => {
    if (tab !== 'campaign') return;
    (async () => {
      try { const r = await getCampaigns(); setCampaigns(Array.isArray(r) ? r : []); }
      catch (e) { setCampaigns([]); }
    })();
    setCampaign(null); setCampaignCreators([]);
  }, [tab]);

  const loadCampaignCreators = async (c) => {
    setCampaign(c);
    if (!c) { setCampaignCreators([]); return; }
    try {
      setLoadingCampaign(true);
      const data = await getCampaignCreators(c.id);
      const rows = (data && (data.results || data.result || data.data)) || [];
      setCampaignCreators(rows.map((r) => normalize(r, platform)).filter(Boolean));
    } catch (e) { setCampaignCreators([]); } finally { setLoadingCampaign(false); }
  };

  // curated lists
  const [lists, setLists] = useState([]);
  const [list, setList] = useState(null);
  const [listInfluencers, setListInfluencers] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  useEffect(() => {
    if (tab !== 'curated') return;
    (async () => {
      try {
        const data = await fetchCuratedList(platform, '', '', 1, 300);
        const cl = (data && data.results && data.results.curated_lists) || (data && data.curated_lists) || {};
        setLists(Array.isArray(cl) ? cl : Object.values(cl));
      } catch (e) { setLists([]); }
    })();
    setList(null); setListInfluencers([]);
  }, [tab, platform]);

  const loadListInfluencers = async (l) => {
    setList(l);
    if (!l) { setListInfluencers([]); return; }
    try {
      setLoadingList(true);
      const data = await fetchCuratedInfluencers(l.id, 1, '');
      const rows = (data && (data.results || data.result || data.data || data.influencers)) || [];
      setListInfluencers(rows.map((r) => normalize(r, platform)).filter(Boolean));
    } catch (e) { setListInfluencers([]); } finally { setLoadingList(false); }
  };

  // selection
  const selectedIds = useMemo(() => new Set(selected.map((c) => c.id)), [selected]);
  const addCreator = (c) => { if (!selectedIds.has(c.id) && selected.length < MAX) setSelected((s) => [...s, c]); };
  const removeCreator = (id) => setSelected((s) => s.filter((c) => c.id !== id));
  const addAll = (rows) => setSelected((s) => {
    const have = new Set(s.map((c) => c.id)); const next = [...s];
    for (const c of rows) { if (next.length >= MAX) break; if (!have.has(c.id)) { next.push(c); have.add(c.id); } }
    return next;
  });
  const canCompare = selected.length >= 2 && selected.length <= MAX;
  const atMax = (id) => selected.length >= MAX && !selectedIds.has(id);

  const listboxProps = { style: { maxHeight: 320 } };

  const table = (rows, loading, emptyNote) => (
    <Box sx={{ flex: 1, mt: 1, border: '1px solid #eef0f3', borderRadius: 2, overflow: 'hidden',
               display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <TableHeader />
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
        {!loading && rows.length === 0 && (
          <Typography sx={{ p: 3, textAlign: 'center', color: '#98a2b3', fontSize: 13 }}>{emptyNote}</Typography>
        )}
        {!loading && rows.map((c) => (
          <CreatorRow key={c.id} c={c} onAdd={addCreator} added={selectedIds.has(c.id)} disabled={atMax(c.id)} />
        ))}
      </Box>
    </Box>
  );

  const searchEmpty = query.trim().length < 2 ? (
    <Box sx={{ flex: 1, mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
               justifyContent: 'center', textAlign: 'center', color: '#98a2b3', p: 4,
               border: '1px dashed #e4e7ec', borderRadius: 2 }}>
      <PersonSearchOutlinedIcon sx={{ fontSize: 40, color: '#c7ccd6', mb: 1 }} />
      <Typography sx={{ fontWeight: 600, color: '#667085' }}>Search creators to add</Typography>
      <Typography sx={{ fontSize: 13, mt: 0.5, maxWidth: 340 }}>
        Type a name to find creators, or switch to <b>Campaign</b> or <b>Curated&nbsp;List</b> to pull a set in one go.
      </Typography>
    </Box>
  ) : table(searchResults, searching, 'No creators found.');

  return (
    <DiscoverThemeProvider>
      <Box className="discover-v2-root"
        sx={{ pb: 2, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 150px)' }}>
        {/* Hero — left-aligned text + graphic on the right */}
        <Box className="dv2-hero" sx={{ mb: 1.5 }}>
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center',
                     justifyContent: 'space-between', gap: 2, textAlign: 'left' }}>
            <Box sx={{ textAlign: 'left' }}>
              <p className="dv2-hero-kicker" style={{ textAlign: 'left' }}>Audience Planning</p>
              <h1 className="dv2-hero-title" style={{ textAlign: 'left' }}>Audience Unique Reach</h1>
              <p className="dv2-hero-sub" style={{ textAlign: 'left' }}>
                Pick 2&ndash;10 creators from anywhere &mdash; search, a campaign, or a curated list &mdash;
                and see how much unique (incremental) vs. redundant audience they add together.
              </p>
            </Box>
            <HeroGraphic />
          </Box>
        </Box>

        {/* Body fills to the bottom; lists scroll inside */}
        <Grid container spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
          <Grid item xs={12} md={8} sx={{ height: '100%' }}>
            <Paper elevation={0}
              sx={{ p: 2, borderRadius: 2.5, border: '1px solid #eceef2', bgcolor: '#fff',
                    height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }} alignItems="center">
                <ToggleButtonGroup size="small" exclusive value={platform} onChange={(_, v) => v && setPlatform(v)}>
                  <ToggleButton value="instagram">Instagram</ToggleButton>
                  <ToggleButton value="youtube">YouTube</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup size="small" exclusive value={tab} onChange={(_, v) => v && setTab(v)}>
                  <ToggleButton value="search">Search</ToggleButton>
                  <ToggleButton value="campaign">Campaign</ToggleButton>
                  <ToggleButton value="curated">Curated list</ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              {tab === 'search' && (
                <>
                  <TextField fullWidth size="small" placeholder="Search creators by name…"
                    value={query} onChange={(e) => setQuery(e.target.value)}
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start">
                        {searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                      </InputAdornment>) }} />
                  {searchEmpty}
                </>
              )}

              {tab === 'campaign' && (
                <>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Autocomplete fullWidth size="small" options={campaigns} value={campaign}
                      onChange={(_, v) => loadCampaignCreators(v)} ListboxProps={listboxProps}
                      getOptionLabel={(o) => o.name || o.title || o.campaign_name || `Campaign ${o.id}`}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      renderInput={(p) => <TextField {...p} placeholder="Search a campaign…"
                        InputProps={{ ...p.InputProps, startAdornment: (<><InputAdornment position="start"><GroupsOutlinedIcon fontSize="small" /></InputAdornment>{p.InputProps.startAdornment}</>) }} />} />
                    {campaignCreators.length > 0 && <Button size="small" onClick={() => addAll(campaignCreators)}>Add all</Button>}
                  </Stack>
                  {table(campaignCreators, loadingCampaign,
                    campaign ? 'No creators in this campaign have discoverable audience profiles.' : 'Pick a campaign to see its creators.')}
                </>
              )}

              {tab === 'curated' && (
                <>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Autocomplete fullWidth size="small" options={lists} value={list}
                      onChange={(_, v) => loadListInfluencers(v)} ListboxProps={listboxProps}
                      getOptionLabel={(o) => o.name || o.title || o.label || `List ${o.id}`}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      renderInput={(p) => <TextField {...p} placeholder="Search a curated list…" />} />
                    {listInfluencers.length > 0 && <Button size="small" onClick={() => addAll(listInfluencers)}>Add all</Button>}
                  </Stack>
                  {table(listInfluencers, loadingList,
                    list ? 'This list has no creators.' : 'Pick a curated list to see its creators.')}
                </>
              )}
            </Paper>
          </Grid>

          {/* Tray */}
          <Grid item xs={12} md={4} sx={{ height: '100%' }}>
            <Paper elevation={0}
              sx={{ p: 2, borderRadius: 2.5, border: '1px solid #eceef2', bgcolor: '#fff',
                    height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Typography sx={{ fontWeight: 700, color: NAVY }}>Selected creators ({selected.length}/{MAX})</Typography>
              <Box sx={{ flex: 1, mt: 1.5, overflowY: 'auto', minHeight: 0 }}>
                {selected.length === 0 ? (
                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center',
                             justifyContent: 'center', textAlign: 'center', color: '#98a2b3' }}>
                    <GroupsOutlinedIcon sx={{ fontSize: 36, color: '#c7ccd6', mb: 1 }} />
                    <Typography sx={{ fontSize: 13, maxWidth: 220 }}>
                      Add 2&ndash;10 creators from the left to compare their unique reach.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {selected.map((c) => (
                      <Chip key={c.id}
                        avatar={<Avatar src={c.picture}>{(c.name || '?').slice(0, 1)}</Avatar>}
                        label={c.name} onDelete={() => removeCreator(c.id)}
                        sx={{ justifyContent: 'space-between', '& .MuiChip-label': { flex: 1 } }} />
                    ))}
                  </Stack>
                )}
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                {selected.length > 0 && <Button color="inherit" onClick={() => setSelected([])}>Clear</Button>}
                <Button fullWidth variant="contained" disabled={!canCompare}
                  startIcon={<InsightsOutlinedIcon />} onClick={() => setOpenCompare(true)}
                  sx={{ bgcolor: NAVY, color: '#fff', '&:hover': { bgcolor: '#0c1128' } }}>
                  Compare Unique Reach
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <AudienceUniqueReachModal open={openCompare} setOpen={setOpenCompare} ids={selected.map((c) => c.id)} />
      </Box>
    </DiscoverThemeProvider>
  );
}
